import { useEffect } from "react";

const SITE_URL = "https://monserrurierbreton.fr";
const SITE_NAME = "Mon Serrurier Breton";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

/**
 * SEO hook — sets <title>, meta description, canonical, Open Graph, Twitter and JSON-LD
 * on the document head. Reverts on unmount.
 *
 * @param {Object} opts
 * @param {string} opts.title         - Full page title (will be used as-is)
 * @param {string} opts.description   - Meta description (~150-160 chars ideal)
 * @param {string} opts.path          - Path relative to SITE_URL (e.g. "/services")
 * @param {string} [opts.image]       - Absolute URL to OG image
 * @param {Array<Object>} [opts.jsonLd] - Array of JSON-LD objects to inject
 * @param {string} [opts.robots]      - Override robots meta (default "index, follow")
 */
export default function useSeo({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  jsonLd = [],
  robots = "index, follow",
}) {
  useEffect(() => {
    const canonical = `${SITE_URL}${path}`;
    const prevTitle = document.title;
    if (title) document.title = title;

    // Helper to ensure a tag exists
    const ensureMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      return el;
    };
    const ensureLink = (rel, attrs = {}) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      return el;
    };

    const previous = {};

    // Description
    const desc = ensureMeta('meta[name="description"]', { name: "description" });
    previous.desc = desc.getAttribute("content");
    if (description) desc.setAttribute("content", description);

    // Robots
    const robotsTag = ensureMeta('meta[name="robots"]', { name: "robots" });
    previous.robots = robotsTag.getAttribute("content");
    robotsTag.setAttribute("content", robots);

    // Canonical
    const canonicalTag = ensureLink("canonical");
    previous.canonical = canonicalTag.getAttribute("href");
    canonicalTag.setAttribute("href", canonical);

    // Open Graph
    const ogTitle = ensureMeta('meta[property="og:title"]', { property: "og:title" });
    previous.ogTitle = ogTitle.getAttribute("content");
    if (title) ogTitle.setAttribute("content", title);

    const ogDesc = ensureMeta('meta[property="og:description"]', { property: "og:description" });
    previous.ogDesc = ogDesc.getAttribute("content");
    if (description) ogDesc.setAttribute("content", description);

    const ogUrl = ensureMeta('meta[property="og:url"]', { property: "og:url" });
    previous.ogUrl = ogUrl.getAttribute("content");
    ogUrl.setAttribute("content", canonical);

    const ogType = ensureMeta('meta[property="og:type"]', { property: "og:type" });
    previous.ogType = ogType.getAttribute("content");
    if (!ogType.getAttribute("content")) ogType.setAttribute("content", "website");

    const ogSite = ensureMeta('meta[property="og:site_name"]', { property: "og:site_name" });
    previous.ogSite = ogSite.getAttribute("content");
    ogSite.setAttribute("content", SITE_NAME);

    const ogLocale = ensureMeta('meta[property="og:locale"]', { property: "og:locale" });
    previous.ogLocale = ogLocale.getAttribute("content");
    ogLocale.setAttribute("content", "fr_FR");

    const ogImage = ensureMeta('meta[property="og:image"]', { property: "og:image" });
    previous.ogImage = ogImage.getAttribute("content");
    if (image) ogImage.setAttribute("content", image);

    // Twitter
    const twCard = ensureMeta('meta[name="twitter:card"]', { name: "twitter:card" });
    previous.twCard = twCard.getAttribute("content");
    twCard.setAttribute("content", "summary_large_image");

    const twTitle = ensureMeta('meta[name="twitter:title"]', { name: "twitter:title" });
    previous.twTitle = twTitle.getAttribute("content");
    if (title) twTitle.setAttribute("content", title);

    const twDesc = ensureMeta('meta[name="twitter:description"]', { name: "twitter:description" });
    previous.twDesc = twDesc.getAttribute("content");
    if (description) twDesc.setAttribute("content", description);

    const twImage = ensureMeta('meta[name="twitter:image"]', { name: "twitter:image" });
    previous.twImage = twImage.getAttribute("content");
    if (image) twImage.setAttribute("content", image);

    document.querySelectorAll("[data-prerender]").forEach((el) => el.remove());
    const ldTags = [];
    (jsonLd || []).forEach((obj) => {
      const tag = document.createElement("script");
      tag.type = "application/ld+json";
      tag.setAttribute("data-seo-hook", "1");
      tag.text = JSON.stringify(obj);
      document.head.appendChild(tag);
      ldTags.push(tag);
    });

    return () => {
      document.title = prevTitle;
      if (previous.desc !== null) desc.setAttribute("content", previous.desc);
      if (previous.robots !== null) robotsTag.setAttribute("content", previous.robots);
      if (previous.canonical !== null) canonicalTag.setAttribute("href", previous.canonical);
      if (previous.ogTitle !== null) ogTitle.setAttribute("content", previous.ogTitle);
      if (previous.ogDesc !== null) ogDesc.setAttribute("content", previous.ogDesc);
      if (previous.ogUrl !== null) ogUrl.setAttribute("content", previous.ogUrl);
      if (previous.ogType !== null) ogType.setAttribute("content", previous.ogType);
      if (previous.ogSite !== null) ogSite.setAttribute("content", previous.ogSite);
      if (previous.ogLocale !== null) ogLocale.setAttribute("content", previous.ogLocale);
      if (previous.ogImage !== null) ogImage.setAttribute("content", previous.ogImage);
      if (previous.twCard !== null) twCard.setAttribute("content", previous.twCard);
      if (previous.twTitle !== null) twTitle.setAttribute("content", previous.twTitle);
      if (previous.twDesc !== null) twDesc.setAttribute("content", previous.twDesc);
      if (previous.twImage !== null) twImage.setAttribute("content", previous.twImage);
      ldTags.forEach((t) => t.parentNode && t.parentNode.removeChild(t));
    };
  }, [title, description, path, image, robots, JSON.stringify(jsonLd)]); // eslint-disable-line react-hooks/exhaustive-deps
}

export const SITE = { url: SITE_URL, name: SITE_NAME };

// Shared LocalBusiness schema fragment
export const localBusinessJsonLd = (opts = {}) => ({
  "@context": "https://schema.org",
  "@type": "Locksmith",
  "@id": `${SITE_URL}/#business`,
  name: SITE_NAME,
  alternateName: "Serrurier Breton Mont-Dol",
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  image: `${SITE_URL}/og-image.jpg`,
  description:
    "Artisan serrurier diplômé basé à Mont-Dol (35120). Dépannage urgence 24h/24, ouverture de porte, changement de serrure, blindage. Intervention en 30 minutes sur le secteur 22 & 35 (Bretagne).",
  telephone: "+33780939300",
  email: "contact@monserrurierbreton.fr",
  priceRange: "€€",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Mont-Dol",
    addressLocality: "Mont-Dol",
    postalCode: "35120",
    addressRegion: "Bretagne",
    addressCountry: "FR",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 48.5567,
    longitude: -1.7561,
  },
  areaServed: [
    { "@type": "AdministrativeArea", name: "Côtes-d'Armor (22)" },
    { "@type": "AdministrativeArea", name: "Ille-et-Vilaine (35)" },
  ],
  serviceArea: {
    "@type": "GeoCircle",
    geoMidpoint: {
      "@type": "GeoCoordinates",
      latitude: 48.5567,
      longitude: -1.7561,
    },
    geoRadius: "30000",
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ],
      opens: "00:00",
      closes: "23:59",
    },
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "127",
    bestRating: "5",
    worstRating: "1",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Services serrurerie",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Ouverture de porte" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Changement de serrure" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Mise en sécurité après effraction" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Blindage de porte" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Cylindre haute sécurité" } },
    ],
  },
  ...opts,
});

// BreadcrumbList helper
export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: it.name,
    item: it.path ? `${SITE_URL}${it.path}` : undefined,
  })),
});
